import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './AdminTranslationPopoverPortal.css';

const VIEWPORT_GAP = 12;
const ANCHOR_GAP = 8;
const COMPACT_VIEWPORT_QUERY = '(max-width: 760px), (pointer: coarse)';

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), Math.max(minimum, maximum));

export const clampTranslationPopoverPosition = ({
  left,
  top,
  panelWidth,
  panelHeight,
  viewportWidth,
  viewportHeight,
  gap = VIEWPORT_GAP,
}) => ({
  left: clamp(left, gap, viewportWidth - panelWidth - gap),
  top: clamp(top, gap, viewportHeight - panelHeight - gap),
});

export const getAnchoredTranslationPopoverPosition = ({
  anchorRect,
  panelWidth,
  panelHeight,
  viewportWidth,
  viewportHeight,
  direction = 'rtl',
}) => {
  const preferredLeft = direction === 'rtl'
    ? anchorRect.right - panelWidth
    : anchorRect.left;
  const below = anchorRect.bottom + ANCHOR_GAP;
  const above = anchorRect.top - panelHeight - ANCHOR_GAP;
  const preferredTop = below + panelHeight <= viewportHeight - VIEWPORT_GAP
    ? below
    : above >= VIEWPORT_GAP
      ? above
      : below;

  return clampTranslationPopoverPosition({
    left: preferredLeft,
    top: preferredTop,
    panelWidth,
    panelHeight,
    viewportWidth,
    viewportHeight,
  });
};

const isCompactViewport = () => (
  typeof window !== 'undefined' && window.matchMedia(COMPACT_VIEWPORT_QUERY).matches
);

const AdminTranslationPopoverPortal = ({
  anchorRef,
  className = '',
  ariaLabel,
  onClose,
  children,
}) => {
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const positionRef = useRef({ left: VIEWPORT_GAP, top: VIEWPORT_GAP, ready: false });
  const [position, setPosition] = useState(positionRef.current);
  const [compact, setCompact] = useState(isCompactViewport);
  const [dragging, setDragging] = useState(false);

  const updatePosition = useCallback((nextPosition) => {
    positionRef.current = { ...nextPosition, ready: true };
    setPosition(positionRef.current);
  }, []);

  const fitToViewport = useCallback(({ anchor = false } = {}) => {
    const panel = panelRef.current;
    const anchorElement = anchorRef?.current;
    if (!panel || !anchorElement || isCompactViewport()) return;

    const panelRect = panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const direction = window.getComputedStyle(anchorElement).direction || 'rtl';
    const nextPosition = anchor || !positionRef.current.ready
      ? getAnchoredTranslationPopoverPosition({
        anchorRect: anchorElement.getBoundingClientRect(),
        panelWidth: panelRect.width,
        panelHeight: panelRect.height,
        viewportWidth,
        viewportHeight,
        direction,
      })
      : clampTranslationPopoverPosition({
        left: positionRef.current.left,
        top: positionRef.current.top,
        panelWidth: panelRect.width,
        panelHeight: panelRect.height,
        viewportWidth,
        viewportHeight,
      });
    updatePosition(nextPosition);
  }, [anchorRef, updatePosition]);

  useLayoutEffect(() => {
    if (compact) return;
    fitToViewport({ anchor: true });
  }, [compact, fitToViewport]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(COMPACT_VIEWPORT_QUERY);
    const handleViewportChange = () => {
      const nextCompact = mediaQuery.matches;
      setCompact(nextCompact);
      if (!nextCompact) window.requestAnimationFrame(() => fitToViewport());
    };
    const handleResize = () => handleViewportChange();

    mediaQuery.addEventListener?.('change', handleViewportChange);
    window.addEventListener('resize', handleResize);
    return () => {
      mediaQuery.removeEventListener?.('change', handleViewportChange);
      window.removeEventListener('resize', handleResize);
    };
  }, [fitToViewport]);

  useEffect(() => {
    if (compact || typeof ResizeObserver === 'undefined' || !panelRef.current) return undefined;
    const observer = new ResizeObserver(() => fitToViewport());
    observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, [compact, fitToViewport]);

  useEffect(() => {
    const closeOnOutside = (event) => {
      if (
        !panelRef.current?.contains(event.target)
        && !anchorRef?.current?.contains(event.target)
      ) onClose();
    };
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('pointerdown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [anchorRef, onClose]);

  useEffect(() => {
    const move = (event) => {
      const drag = dragRef.current;
      const panel = panelRef.current;
      if (!drag || !panel || event.pointerId !== drag.pointerId) return;
      const panelRect = panel.getBoundingClientRect();
      updatePosition(clampTranslationPopoverPosition({
        left: drag.left + event.clientX - drag.clientX,
        top: drag.top + event.clientY - drag.clientY,
        panelWidth: panelRect.width,
        panelHeight: panelRect.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      }));
    };
    const stop = (event) => {
      if (!dragRef.current || event.pointerId !== dragRef.current.pointerId) return;
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, [updatePosition]);

  const startDrag = (event) => {
    if (compact || event.button !== 0 || event.target.closest('button, input, textarea, select, a')) return;
    const panel = panelRef.current;
    if (!panel) return;
    event.preventDefault();
    const panelRect = panel.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      left: panelRect.left,
      top: panelRect.top,
    };
    setDragging(true);
  };

  if (typeof document === 'undefined') return null;

  const desktopStyle = {
    position: 'fixed',
    zIndex: 11000,
    inset: 'auto',
    left: `${position.left}px`,
    top: `${position.top}px`,
    maxHeight: `calc(100dvh - ${VIEWPORT_GAP * 2}px)`,
    overflowY: 'auto',
    visibility: position.ready ? 'visible' : 'hidden',
  };
  const compactStyle = {
    position: 'fixed',
    zIndex: 11000,
    inset: `${VIEWPORT_GAP}px`,
    width: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    overflowY: 'auto',
    visibility: 'visible',
  };

  return createPortal(
    <div
      ref={panelRef}
      className={`admin-translation-popover-portal ${dragging ? 'is-dragging' : ''} ${compact ? 'is-compact' : ''} ${className}`.trim()}
      style={compact ? compactStyle : desktopStyle}
      role="dialog"
      aria-modal="false"
      aria-label={ariaLabel}
      onClick={(event) => event.stopPropagation()}
    >
      {children({
        dragHandleProps: {
          onPointerDown: startDrag,
          'data-translation-drag-handle': 'true',
          title: compact ? undefined : 'برای جابه‌جایی پنجره بکشید',
        },
        compact,
      })}
    </div>,
    document.body
  );
};

export default AdminTranslationPopoverPortal;
